/* eslint-disable react-refresh/only-export-components */
import '@/styles/global.scss'
import '@loc/one-ui/dist/style.css'

import { get } from 'lodash-es'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'

import type { EHostKey } from '@loc/hosts/src/constants'
import type { AppContext, AppType } from 'next/app'
import type { TNextPagePro } from '@/types/global'

import ErrorBoundary from '@/components/ErrorBoundary'
import { SiteContextProvider } from '@/contexts/SiteContext'
import { fetchCacheData } from '@/helpers/cache-data/fetch'
import { IS_DEV, isBrowser } from '@/helpers/env'
import { cacheResolveHost, getSelfServerOrigin } from '@/helpers/host'
import { api } from '@/utils/api'
import { dynamicAxios } from '@/helpers/dynamics/axios'
import { cacheUaParser } from '@/helpers/ua-parser'
import AppSessionProvider from '@/_blocks/AppSessionProvider'
import SiteGlobalComponent from '@/components/SiteGlobalComponent'
import GlobalErrorHandler from '@/components/GlobalErrorHandler'
import { AppUIModalProvider } from '@/contexts/AppUIModalContext'
import CoreDataScript from '@/contexts/SiteSpecificContext/CoreDataScript'
import { useAutoUpdateSubUsage } from '@/hooks/useAutoUpdateSubUsage'
import { loadTranslation } from '~/i18n'
import LinguiI18nProvider from '@/components/LinguiI18nProvider'

const ClientErrorPage = dynamic(() => import('./_client-error'), {
  ssr: false,
})
const DevClickToComponent = dynamic(
  () =>
    import('click-to-react-component').then(
      (module) => module.ClickToComponent,
    ),
  {
    ssr: false,
  },
)

// 自动更新用户使用量的包装组件
const AutoUpdateSubUsageWrapper = ({
  children,
}: {
  children: React.ReactNode
}) => {
  useAutoUpdateSubUsage()
  return <>{children}</>
}

const App: AppType<
  Awaited<ReturnType<typeof _getInitialProps>>['pageProps']
> = (props) => {
  const { router, Component, pageProps: _pageProps } = props
  const { _extraProps, initialProps, ...pageProps } = _pageProps
  const { resolvedHost, cacheData, noPageKey, browserInfo, _translation } =
    _extraProps || {}

  const { locale, translation } = _extraProps

  const errorBoundaryRef = useRef<ErrorBoundary>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_PREVIEW === 'true') {
      // eslint-disable-next-line no-console
      console.log('--->[mode: PREVIEW]<---')
    }
    // eslint-disable-next-line no-console
    console.log('💎 BUILD_ID', window.__NEXT_DATA__.buildId)
  }, [])

  useEffect(() => {
    if (!_translation) {
      return
    }
    ;(window as any)._translation = _translation
  }, [_translation])

  // support mobile debug tool
  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_APP_ENV !== 'production' &&
      window.location.search.includes('debug=true')
    ) {
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/eruda'
      script.onload = () => {
        ;(window as any).eruda.init()
      }
      document.body.appendChild(script)
    }
  }, [])

  // 当渲染 ErrorBoundary 错误页面时，如果点击返回时默认仍然会显示错误页面，
  // 需要在页面改变前重置 ErrorBoundary 状态
  useEffect(() => {
    if (!hasError) {
      return
    }

    const handleRouteStart = () => {
      errorBoundaryRef.current?.reset()
    }
    router.events.on('routeChangeStart', handleRouteStart)

    return () => {
      router.events.off('routeChangeStart', handleRouteStart)
    }
  }, [router.events, hasError])

  const enableClickToComponent = process.env.NODE_ENV === 'development'

  if (!resolvedHost) {
    // 如果没有通过 router.route 匹配到应用配置
    // 则说明访问的地址非法，此时对应的 Component 为 Next.js 默认的 404/500 页面
    // 或者开发环境通过 localhost 可访问的页面，比如 /_images
    // 且 404/500 页面通常不会出现客户端异常
    return (
      <>
        <CoreDataScript />
        <AppSessionProvider>
          <ErrorBoundary
            ref={errorBoundaryRef}
            onError={() => {
              setHasError(true)
            }}
            errorRender={<ClientErrorPage />}
          >
            <LinguiI18nProvider locale={locale} translation={translation}>
              <Component {...initialProps} {...pageProps} />
            </LinguiI18nProvider>
          </ErrorBoundary>
        </AppSessionProvider>
      </>
    )
  }

  return (
    <>
      {enableClickToComponent && <DevClickToComponent editor='cursor' />}
      <CoreDataScript />
      <SiteContextProvider
        browserInfo={browserInfo}
        resolvedHost={resolvedHost}
        cacheData={cacheData}
      >
        <AppSessionProvider>
          <ErrorBoundary
            ref={errorBoundaryRef}
            onError={() => {
              setHasError(true)
            }}
            errorRender={<ClientErrorPage />}
          >
            <AppUIModalProvider>
              <LinguiI18nProvider locale={locale} translation={translation}>
                <AutoUpdateSubUsageWrapper>
                  {/* 避免页面切换时部分组件不支持状态更新导致异常，统一添加 key 解决 */}
                  <Component
                    key={noPageKey ? undefined : `${router.asPath}`}
                    {...initialProps}
                    {...pageProps}
                  />
                  <GlobalErrorHandler />
                  <SiteGlobalComponent />
                </AutoUpdateSubUsageWrapper>
              </LinguiI18nProvider>
            </AppUIModalProvider>
          </ErrorBoundary>
        </AppSessionProvider>
      </SiteContextProvider>
    </>
  )
}

/**
 * 测试发现客户端服务端总是会调用，仅在页面在浏览器中返回时不会触发服务端逻辑
 */
async function _getInitialProps(appCtx: AppContext) {
  const url = isBrowser() ? window.location.href : appCtx.ctx.req?.url || ''
  const hostname =
    appCtx.ctx.req?.headers.host ||
    window?.location?.hostname
      ?.replace('preview.', '')
      .replace('preview-', '') ||
    ''
  if (isBrowser()) {
    throw new Error('getInitialProps should not be used in browser')
  }
  const host = appCtx.ctx.req?.headers.host || window.location.host
  const resolvedHost = await cacheResolveHost(host)

  const pagePath = appCtx.ctx.pathname
  const locale = appCtx.ctx.locale

  const pureAppPage = (appCtx.Component as TNextPagePro)?.pureAppPage || false
  const noPageKey: boolean = get(appCtx.Component, 'noPageKey') || false
  const cacheDataKeys = get(appCtx.Component, 'cacheDataKeys')
  const getInitialProps = async () => {
    if (appCtx.Component.getInitialProps) {
      const result = await appCtx.Component.getInitialProps(appCtx.ctx)
      return result
    }
    return {}
  }

  const browserInfo = await cacheUaParser(
    isBrowser() ? navigator?.userAgent : appCtx.ctx.req?.headers['user-agent'],
  )

  const isI18nInspectMode =
    process.env.NEXT_PUBLIC_APP_ENV === 'test' && url.includes('_i18n_inspect')

  const getTranslation = async () => {
    if (pureAppPage) {
      return {}
    }
    if (isBrowser()) {
      return {}
    }
    if (IS_DEV && hostname.includes('localhost') && pagePath === '/_error') {
      return {}
    }
    const translationLocale = isI18nInspectMode ? 'en' : locale

    const translation = await loadTranslation(
      translationLocale,
      resolvedHost.hostKey as EHostKey,
      pagePath,
    )
    return translation
  }

  const [cacheData, initialProps, translation] = await Promise.all([
    fetchCacheData(resolvedHost.hostKey, cacheDataKeys),
    getInitialProps(),
    getTranslation(),
  ])

  const coreAppPage = (appCtx.Component as TNextPagePro)?.coreAppPage

  if (IS_DEV) {
    await dynamicAxios((axios) => {
      return axios.post(
        `${getSelfServerOrigin()}/api/_dev`,
        {},
        {
          proxy: false,
        },
      )
    })
  }

  const _extraProps = {
    resolvedHost,
    cacheData,
    noPageKey,
    browserInfo,
    translation: isI18nInspectMode ? {} : translation,
    ...(isI18nInspectMode ? { _translation: translation } : {}),
    locale,
    coreAppPage,
    pureAppPage,
  }

  return {
    // 方便类型推导
    _extraProps,
    initialProps,
    pageProps: {
      // 实际上 trpc 目前只能通过 pageProps 内部传值的方式传入数据
      // ref: https://github.dev/trpc/trpc/blob/04a9dc64097a4a9594ba5b09b68f7a3ae6404fde/packages/next/src/withTRPC.tsx#L162-L163
      _extraProps,
      initialProps,
    },
  }
}

App.getInitialProps = _getInitialProps

export default api.withTRPC(App)
